import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface ProjectSummary {
  id: string;
  name: string;
  startDate: string;
  companyId: string | null;
  createdBy: string | null;
  createdAt: string;
  milestoneCount: number;
  completedCount: number;
  overallProgress: number;
}

export interface ProjectMilestone {
  id: string;
  projectId: string;
  name: string;
  defaultOffsetDays: number | null;
  priority: "High" | "Medium" | "Low" | null;
  duration: number | null;
  startDate: string | null;
  dueDate: string | null;
  progress: number | null;
  lastProgressAt: string | null;
  owner: string | null;
  sortOrder: number | null;
  linkedTo: string[];
  tasks: ProjectTask[];
}

export interface ProjectTask {
  id: string;
  milestoneId: string;
  projectId: string;
  name: string;
  assigneeId: string | null;
  assigneeName: string | null;
  status: "todo" | "in_progress" | "done" | null;
  startDate: string | null;
  dueDate: string | null;
  percentComplete: number | null;
  lastProgressAt: string | null;
  sortOrder: number | null;
}

export interface ProjectActivityLog {
  id: string;
  projectId: string;
  action: string;
  details: string | null;
  type: "info" | "success" | "warning" | "error" | null;
  userId: string | null;
  createdAt: string;
}

export interface ProjectFull {
  id: string;
  name: string;
  startDate: string;
  companyId: string | null;
  stagnationThresholdDays: number | null;
  createdBy: string | null;
  createdAt: string;
  milestones: ProjectMilestone[];
  phases: { id: string; name: string; expectedDuration: string | null; sortOrder: number | null }[];
  teamMembers: string[];
  teamMemberRecords: { id: string; name: string; userId: string | null }[];
  calendarNotes: Record<string, string>;
  activityLogs: ProjectActivityLog[];
}

async function fetchJson(url: string, options?: RequestInit) {
  const res = await fetch(url, { credentials: "include", ...options });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Request failed" }));
    throw new Error(err.error || "Request failed");
  }
  return res.json();
}

export function useProjects() {
  return useQuery<ProjectSummary[]>({
    queryKey: ["projects"],
    queryFn: () => fetchJson("/api/projects"),
  });
}

export function useProject(id: string | undefined) {
  return useQuery<ProjectFull>({
    queryKey: ["project", id],
    queryFn: () => fetchJson(`/api/projects/${id}`),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; startDate?: string; dealType: string; companyId?: string }) =>
      fetchJson("/api/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projects"] }),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchJson(`/api/projects/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projects"] }),
  });
}

export function useUpdateProject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name?: string; startDate?: string; recalculateDates?: boolean; companyId?: string; stagnationThresholdDays?: number | null }) =>
      fetchJson(`/api/projects/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project", id] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useUpdateMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ milestoneId, ...data }: { milestoneId: string; [key: string]: any }) =>
      fetchJson(`/api/projects/${projectId}/milestones/${milestoneId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useCreateMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; startDate?: string; dueDate?: string; priority?: string; duration?: number; owner?: string }) =>
      fetchJson(`/api/projects/${projectId}/milestones`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectId] }),
  });
}

export function useDeleteMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (milestoneId: string) =>
      fetchJson(`/api/projects/${projectId}/milestones/${milestoneId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectId] }),
  });
}

export function useBulkUpdateMilestones(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (updates: { id: string; dueDate: string }[]) =>
      fetchJson(`/api/projects/${projectId}/milestones/bulk-update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectId] }),
  });
}

export function useAddTeamMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { userId: string }) =>
      fetchJson(`/api/projects/${projectId}/team`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectId] }),
  });
}

export function useRemoveTeamMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      fetchJson(`/api/projects/${projectId}/team/${memberId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectId] }),
  });
}

export function useUpdateCalendarNote(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ milestoneId, note }: { milestoneId: string; note: string }) =>
      fetchJson(`/api/projects/${projectId}/milestones/${milestoneId}/notes`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectId] }),
  });
}

export function useCreateTask(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { milestoneId: string; name: string; assigneeId?: string; assigneeName?: string; startDate?: string; dueDate?: string; percentComplete?: number }) =>
      fetchJson(`/api/projects/${projectId}/milestones/${data.milestoneId}/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectId] }),
  });
}

export function useUpdateTask(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, ...data }: { taskId: string; name?: string; assigneeId?: string; assigneeName?: string; status?: string; startDate?: string | null; dueDate?: string | null; percentComplete?: number }) =>
      fetchJson(`/api/projects/${projectId}/tasks/${taskId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectId] }),
  });
}

export function useDeleteTask(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) =>
      fetchJson(`/api/projects/${projectId}/tasks/${taskId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectId] }),
  });
}

// Client-specific hooks

export function useClientProject(companyId: string | undefined) {
  return useQuery<ProjectFull | null>({
    queryKey: ["client-project", companyId],
    queryFn: () => fetchJson(`/api/companies/${companyId}/project`),
    enabled: !!companyId,
  });
}

export interface LinkableProject {
  id: string;
  name: string;
  companyId: string | null;
  clientName: string | null;
}

export function useLinkableProjects(search: string) {
  return useQuery<LinkableProject[]>({
    queryKey: ["linkable-projects", search],
    queryFn: () => fetchJson(`/api/projects/linkable?search=${encodeURIComponent(search || "")}`),
  });
}

export function useLinkProject(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) =>
      fetchJson(`/api/companies/${companyId}/link-project`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-project", companyId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["linkable-projects"] });
    },
  });
}

export function useUnlinkProject(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) =>
      fetchJson(`/api/companies/${companyId}/unlink-project`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-project", companyId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["linkable-projects"] });
    },
  });
}

export interface BuyerLink {
  linkId: string;
  buyerId: string;
  buyerName: string;
  buyerFirmType: string | null;
  buyerIndustry: string | null;
  buyerHQ: string | null;
  stage: string | null;
  status: string | null;
  notes: string | null;
  addedByName: string | null;
  createdAt: string;
}

export function useClientBuyers(companyId: string | undefined) {
  return useQuery<BuyerLink[]>({
    queryKey: ["buyer-links", companyId],
    queryFn: () => fetchJson(`/api/companies/${companyId}/buyer-links`),
    enabled: !!companyId,
  });
}

export function useAddBuyerLink(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { buyerId: string; stage?: string; notes?: string }) =>
      fetchJson(`/api/companies/${clientId}/buyer-links`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["buyer-links", clientId] });
      qc.invalidateQueries({ queryKey: ["company-activities", clientId] });
    },
  });
}

export function useUpdateBuyerLink(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId, ...data }: { linkId: string; stage?: string; status?: string; notes?: string }) =>
      fetchJson(`/api/buyer-links/${linkId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["buyer-links", clientId] }),
  });
}

export function useRemoveBuyerLink(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) =>
      fetchJson(`/api/buyer-links/${linkId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["buyer-links", clientId] }),
  });
}

export function useHoldClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, holdReason, holdRevisitDate }: { companyId: string; holdReason?: string; holdRevisitDate?: string }) =>
      fetchJson(`/api/companies/${companyId}/hold`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ holdReason, holdRevisitDate }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["company"] });
      qc.invalidateQueries({ queryKey: ["financials-fee-detail"] });
    },
  });
}

export function useReactivateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) =>
      fetchJson(`/api/companies/${companyId}/reactivate`, { method: "PUT" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["company"] });
      qc.invalidateQueries({ queryKey: ["financials-fee-detail"] });
    },
  });
}

// ─── Share Link Hooks ────────────────────────────────────────────────────────

export interface ShareSettings {
  showStats: boolean;
  showCalendar: boolean;
  showTimeline: boolean;
  showTracker: boolean;
}

export interface ShareLinkInfo {
  id: string;
  url: string;
  createdAt: string;
  expiresAt: string;
  lastViewedAt: string | null;
  viewCount: number;
  distinctIpCount: number;
}

export function useShareLink(projectId: string) {
  return useQuery<{ link: ShareLinkInfo | null; settings?: ShareSettings }>({
    queryKey: ["project-share-link", projectId],
    queryFn: () => fetchJson(`/api/projects/${projectId}/share-link`),
    refetchOnWindowFocus: true,
  });
}

export function useCreateShareLink(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetchJson(`/api/projects/${projectId}/share-link`, { method: "POST" }) as Promise<ShareLinkInfo>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project-share-link", projectId] }),
  });
}

export function useRevokeShareLink(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) =>
      fetchJson(`/api/projects/${projectId}/share-link/${linkId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project-share-link", projectId] }),
  });
}

export function useExtendShareLink(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { linkId: string; extendDays: number }) =>
      fetchJson(`/api/projects/${projectId}/share-link/${args.linkId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ extendDays: args.extendDays }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project-share-link", projectId] }),
  });
}

export function useUpdateShareSettings(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<ShareSettings>) =>
      fetchJson(`/api/projects/${projectId}/share-settings`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      }) as Promise<{ settings: ShareSettings }>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project-share-link", projectId] }),
  });
}
